/**
 * The arithmetic every projectile in the simulator shares: drag, gravity, and hitting things.
 *
 * Pure functions over plain numbers and `Vector3`s, deliberately. A cannon round and a missile
 * disagree about almost everything — one is inert and one steers, one lives for three seconds and
 * one for twenty — but they agree completely about what the air does to a body moving through it,
 * and that agreement is worth exactly one module. It also means the part of the weapons system
 * with real physics in it can be tested without constructing a gun, an aircraft or a scene.
 *
 * ## Frames
 *
 * Everything here is sim-frame **NED**: x north, y east, **z down**. So gravity is `+z`, and a
 * round that is falling has a positive `z` velocity. Getting this backwards produces a gun whose
 * rounds rise, which is a funny bug exactly once.
 *
 * ## The drag model, and why it is one number
 *
 * A projectile's deceleration from drag is
 *
 * ```
 *   a_drag = ½ · ρ · v² · C_D · A / m        [m/s², opposing the velocity]
 * ```
 *
 * Every term but ρ and v is a constant of the projectile, so they are collapsed into one:
 *
 * ```
 *   k = ½ · C_D · A / m                      [m²/kg]
 *   a_drag = k · ρ · v²
 * ```
 *
 * which is the inverse of the ballistic coefficient, give or take the conventions gunners use.
 * Keeping ρ out of it is not tidiness — a round fired at 30,000 ft travels visibly further than
 * the same round at sea level, because ρ is a third of what it was, and folding the sea-level
 * density into `k` would throw that away.
 *
 * The quadratic form is right for the regime that matters here. A 20 mm round leaves the barrel
 * at about Mach 3 and spends its whole useful life supersonic, where C_D is roughly flat; the
 * transonic bucket it would eventually fall into is past the range at which anyone is aiming.
 *
 * `updateFixed` / `updateFrame`: neither — this module is called *by* things that have one. Every
 * function takes its output object from the caller and allocates nothing.
 */

import { Vector3 } from 'three';
import { G0 } from '../../core/math.js';

/** Sea-level ISA density [kg/m³], for the handful of callers that want a reference figure. */
export const SEA_LEVEL_DENSITY_KG_PER_M3 = 1.225;

/**
 * Inverse ballistic coefficient `k = ½·C_D·A/m` [m²/kg] for a body of a given calibre and mass.
 *
 * Spelled as a function rather than left to each config to precompute because the interesting
 * quantity to *author* is "a 20 mm round weighing 100 g", and the interesting quantity to
 * *integrate* is `k`. Doing the conversion here means a config never contains a number nobody
 * can sanity-check by eye.
 */
export function inverseBallisticCoefficient(
  diameterM: number,
  massKg: number,
  dragCoefficient: number,
): number {
  const radius = diameterM * 0.5;
  const frontalAreaM2 = Math.PI * radius * radius;
  return (0.5 * dragCoefficient * frontalAreaM2) / massKg;
}

/**
 * Advance an unpowered projectile one step: gravity, quadratic drag, semi-implicit Euler.
 *
 * Semi-implicit — velocity first, then position from the *new* velocity — for the same reason the
 * flight model uses it: it is the cheapest integrator that does not pump energy, and a gun firing
 * a hundred rounds a second cannot afford anything cleverer. At 120 Hz a 1000 m/s round moves 8 m
 * a step, and the truncation error over a three-second flight is centimetres.
 *
 * @param position  Sim-frame NED position [m]. Mutated.
 * @param velocity  Sim-frame NED velocity [m/s]. Mutated.
 * @param inverseBc `k` from {@link inverseBallisticCoefficient} [m²/kg].
 * @param densityKgPerM3 Air density at the projectile [kg/m³].
 * @param dt        Step [s].
 */
export function integrateBallistic(
  position: Vector3,
  velocity: Vector3,
  inverseBc: number,
  densityKgPerM3: number,
  dt: number,
): void {
  const speed = velocity.length();
  if (speed > 1e-6) {
    // a_drag = k·ρ·v², opposing the velocity. Written as a per-component scale of the velocity
    // vector so no unit vector has to be materialised: (v/|v|)·|v|² = v·|v|.
    const decel = inverseBc * densityKgPerM3 * speed * dt;
    velocity.x -= velocity.x * decel;
    velocity.y -= velocity.y * decel;
    velocity.z -= velocity.z * decel;
  }
  // NED: down is +z, so gravity adds.
  velocity.z += G0 * dt;

  position.x += velocity.x * dt;
  position.y += velocity.y * dt;
  position.z += velocity.z * dt;
}

/**
 * Where along a step's travel a projectile first touches a sphere, or −1 for a clean miss.
 *
 * **This has to be swept, and that is the whole reason the function exists.** A 20 mm round at
 * 1036 m/s covers 8.6 m in a 120 Hz tick and a drone is about 6 m across, so a test that asked
 * "is the round inside the target *now*" would miss more often than it hit, and would miss more
 * often the faster the round was going — a gun that works badly at exactly the ranges it is meant
 * for. So the round's motion over the step is treated as the segment it is, and the segment is
 * intersected with the target's sphere analytically.
 *
 * Returns the fraction along `p0 → p1` of the first contact, in `[0, 1]`, or −1. A round that
 * starts inside the sphere returns 0.
 *
 * @param p0     Segment start, sim-frame NED [m].
 * @param p1     Segment end [m].
 * @param centre Sphere centre [m].
 * @param radiusM Sphere radius [m].
 */
export function segmentSphereHit(
  p0: Vector3,
  p1: Vector3,
  centre: Vector3,
  radiusM: number,
): number {
  // The classic quadratic: |p0 + t·d − c|² = r², solved for the smaller root in [0, 1].
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const dz = p1.z - p0.z;
  const fx = p0.x - centre.x;
  const fy = p0.y - centre.y;
  const fz = p0.z - centre.z;

  const a = dx * dx + dy * dy + dz * dz;
  // A projectile that did not move this step cannot enter anything it was not already in.
  if (a < 1e-12) {
    return fx * fx + fy * fy + fz * fz <= radiusM * radiusM ? 0 : -1;
  }

  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const c = fx * fx + fy * fy + fz * fz - radiusM * radiusM;
  if (c <= 0) return 0; // started inside

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return -1;

  const root = Math.sqrt(discriminant);
  // `c > 0` here, so the near root is the one with the minus sign and is the first contact.
  const t = (-b - root) / (2 * a);
  return t >= 0 && t <= 1 ? t : -1;
}

/**
 * A direction sampled uniformly inside a cone about `axis`, for gun dispersion.
 *
 * Uniform **over the cone's solid angle**, not over the polar angle: sampling `θ` uniformly in
 * `[0, θmax]` piles rounds up along the axis and leaves the rim thin, which reads as a gun that is
 * more accurate than it is. The correct sampling is `cos θ` uniform in `[cos θmax, 1]`, and the
 * difference is visible in a burst — a real gun's group is an even smear, not a bullseye.
 *
 * The two perpendicular axes are the caller's, because a gun already has a right and an up and
 * building an arbitrary orthonormal basis here would be both wasted work and a source of roll
 * ambiguity in the pattern.
 *
 * @param axis   Unit vector down the bore.
 * @param right  Unit vector perpendicular to `axis`.
 * @param up     Unit vector perpendicular to both.
 * @param coneHalfAngleRad Half-angle of the dispersion cone [rad].
 * @param u1     Uniform random in [0, 1).
 * @param u2     Uniform random in [0, 1).
 * @param out    Receives the sampled unit direction.
 */
export function sampleCone(
  axis: Vector3,
  right: Vector3,
  up: Vector3,
  coneHalfAngleRad: number,
  u1: number,
  u2: number,
  out: Vector3,
): Vector3 {
  const cosMax = Math.cos(coneHalfAngleRad);
  const cosTheta = cosMax + u1 * (1 - cosMax);
  const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
  const phi = u2 * Math.PI * 2;
  const cx = Math.cos(phi) * sinTheta;
  const cy = Math.sin(phi) * sinTheta;

  out.x = axis.x * cosTheta + right.x * cx + up.x * cy;
  out.y = axis.y * cosTheta + right.y * cx + up.y * cy;
  out.z = axis.z * cosTheta + right.z * cx + up.z * cy;
  return out;
}

/**
 * Time of flight for a round fired at `muzzleSpeedMps` to reach `rangeM`, drag included.
 *
 * The closed-form answer for pure quadratic drag along a straight line. Speed obeys
 * `dv/dt = −kρv²`, which integrates to `v(t) = v₀/(1 + kρv₀t)`, and integrating *that* gives
 * distance `s(t) = ln(1 + kρv₀t)/(kρ)`. Inverting for `t`:
 *
 * ```
 *   t = (e^{kρs} − 1) / (kρ · v₀)
 * ```
 *
 * Used by the gun sight, where it is exactly what is needed and a numerical integration would be
 * absurd: the LCOS has to answer "how long would a round take to get there" every frame, for one
 * range, and this is that answer in four operations.
 *
 * The straight-line assumption is the sight's own assumption too — the drop is applied afterwards
 * as a separate term — so this is not an approximation the sight has to apologise for.
 */
export function ballisticTimeOfFlight(
  rangeM: number,
  muzzleSpeedMps: number,
  inverseBc: number,
  densityKgPerM3: number,
): number {
  const kRho = inverseBc * densityKgPerM3;
  if (kRho < 1e-12 || muzzleSpeedMps < 1e-6) {
    return muzzleSpeedMps < 1e-6 ? 0 : rangeM / muzzleSpeedMps;
  }
  return (Math.exp(kRho * rangeM) - 1) / (kRho * muzzleSpeedMps);
}
